import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { LookupCategoryResponse, LookupEntryResponse } from '../types';

interface EntryFormData {
  code: string;
  name: string;
  nameAr: string;
  parentEntryId: string;
  sortOrder: string;
}

const emptyForm: EntryFormData = {
  code: '',
  name: '',
  nameAr: '',
  parentEntryId: '',
  sortOrder: '',
};

export default function LookupsPage() {
  const auth = useAuth();
  const [categories, setCategories] = useState<LookupCategoryResponse[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [entries, setEntries] = useState<LookupEntryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Parent filter for hierarchical categories (e.g., Areas filtered by City)
  const [parentFilter, setParentFilter] = useState<string>('');
  const [parentEntries, setParentEntries] = useState<LookupEntryResponse[]>([]);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [form, setForm] = useState<EntryFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);
  // #1042: hierarchy comes from the data (category -> parent category), not a hard-coded AREAS -> CITIES.
  const parentCategory = categories.find(c => c.id === selectedCategory?.parentCategoryId);
  const isHierarchical = !!parentCategory;
  const ORPHANS = '__orphans__';

  // Load categories
  useEffect(() => {
    if (!auth.token) return;
    setLoading(true);
    api.fetchAdminCategories(auth.token)
      .then(cats => {
        setCategories(cats);
        if (cats.length > 0 && !selectedCategoryId) {
          setSelectedCategoryId(cats[0].id);
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.token]);

  // Load entries when category changes
  const loadEntries = useCallback(async () => {
    if (!selectedCategoryId || !auth.token) return;
    setLoadingEntries(true);
    setError(null);

    try {
      const cat = categories.find(c => c.id === selectedCategoryId);
      if (!cat) return;

      // For child categories, also load the parent category's entries
      const parentCat = categories.find(c => c.id === cat.parentCategoryId);
      if (parentCat) {
        const parents = await api.get<LookupEntryResponse[]>(
          `/api/admin/lookups/categories/${parentCat.id}/entries`,
          auth.token
        );
        setParentEntries(parents);
      } else {
        setParentEntries([]);
        setParentFilter('');
      }

      const allEntries = await api.get<LookupEntryResponse[]>(
        `/api/admin/lookups/categories/${selectedCategoryId}/entries`,
        auth.token
      );
      setEntries(allEntries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load entries');
    } finally {
      setLoadingEntries(false);
    }
  }, [selectedCategoryId, auth.token, categories]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Filter entries by parent
  const isOrphan = (e: LookupEntryResponse) => isHierarchical && !e.parentEntryId;
  const orphanCount = entries.filter(isOrphan).length;
  const filteredEntries = parentFilter === ORPHANS
    ? entries.filter(isOrphan)
    : parentFilter
      ? entries.filter(e => e.parentEntryId === parentFilter)
      : entries;

  // Sort by sortOrder
  const sortedEntries = [...filteredEntries].sort((a, b) => a.sortOrder - b.sortOrder);

  const handleSelectCategory = (catId: string) => {
    setSelectedCategoryId(catId);
    setShowForm(false);
    setEditingEntryId(null);
    setForm(emptyForm);
    setParentFilter('');
  };

  const handleAddNew = () => {
    setEditingEntryId(null);
    setForm({
      ...emptyForm,
      parentEntryId: parentFilter && parentFilter !== ORPHANS ? parentFilter : '',
      sortOrder: String(entries.length + 1),
    });
    setShowForm(true);
  };

  const handleEdit = (entry: LookupEntryResponse) => {
    setEditingEntryId(entry.id);
    setForm({
      code: entry.code,
      name: entry.name,
      nameAr: entry.nameAr || '',
      parentEntryId: entry.parentEntryId || '',
      sortOrder: String(entry.sortOrder),
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!auth.token || !selectedCategoryId) return;
    setSaving(true);
    setError(null);

    try {
      if (editingEntryId) {
        // Update
        await api.updateLookupEntry(editingEntryId, {
          name: form.name,
          nameAr: form.nameAr || undefined,
          sortOrder: form.sortOrder ? parseInt(form.sortOrder, 10) : undefined,
          parentEntryId: form.parentEntryId || undefined,
        }, auth.token);
      } else {
        // Create
        await api.createLookupEntry(selectedCategoryId, {
          code: form.code,
          name: form.name,
          nameAr: form.nameAr || undefined,
          parentEntryId: form.parentEntryId || undefined,
          sortOrder: form.sortOrder ? parseInt(form.sortOrder, 10) : undefined,
        }, auth.token);
      }

      setShowForm(false);
      setEditingEntryId(null);
      setForm(emptyForm);
      await loadEntries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (entry: LookupEntryResponse) => {
    if (!auth.token) return;
    setError(null);
    try {
      if (entry.isActive) {
        await api.deactivateLookupEntry(entry.id, auth.token);
      } else {
        await api.activateLookupEntry(entry.id, auth.token);
      }
      await loadEntries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0 || !auth.token || !selectedCategoryId) return;
    const newEntries = [...sortedEntries];
    [newEntries[index - 1], newEntries[index]] = [newEntries[index], newEntries[index - 1]];
    const orderedIds = newEntries.map(e => e.id);
    try {
      await api.reorderLookupEntries(selectedCategoryId, orderedIds, auth.token);
      await loadEntries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder');
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === sortedEntries.length - 1 || !auth.token || !selectedCategoryId) return;
    const newEntries = [...sortedEntries];
    [newEntries[index], newEntries[index + 1]] = [newEntries[index + 1], newEntries[index]];
    const orderedIds = newEntries.map(e => e.id);
    try {
      await api.reorderLookupEntries(selectedCategoryId, orderedIds, auth.token);
      await loadEntries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p>Loading lookups...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-card-header" style={{ marginBottom: '1.5rem' }}>
        <h1 className="admin-card-title">Lookups Management</h1>
      </div>

      {error && (
        <div className="admin-info-banner" style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '1.5rem', alignItems: 'start' }}>
        {/* Left Panel: Category List */}
        <div className="admin-card" style={{ padding: 0 }}>
          <div style={{ padding: '1rem', borderBottom: '1px solid var(--admin-border)' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--admin-text)', margin: 0 }}>
              Categories
            </h3>
          </div>
          <div>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => handleSelectCategory(cat.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.75rem 1rem',
                  background: selectedCategoryId === cat.id ? 'var(--admin-primary)' : 'transparent',
                  color: selectedCategoryId === cat.id ? '#fff' : 'var(--admin-text)',
                  border: 'none',
                  borderBottom: '1px solid var(--admin-border)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: selectedCategoryId === cat.id ? 600 : 400,
                  transition: 'background 0.15s',
                }}
              >
                {cat.name}
                <span style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  color: selectedCategoryId === cat.id ? 'rgba(255,255,255,0.7)' : 'var(--admin-text-muted)',
                  marginTop: '0.125rem',
                }}>
                  {cat.code}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Right Panel: Entries */}
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--admin-text)', margin: 0 }}>
              {selectedCategory?.name || 'Select a category'} Entries
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {/* Parent Filter for hierarchical categories */}
              {isHierarchical && parentEntries.length > 0 && (
                <select
                  className="admin-select"
                  value={parentFilter}
                  onChange={(e) => setParentFilter(e.target.value)}
                  style={{ minWidth: '160px' }}
                >
                  <option value="">All {parentCategory?.name}</option>
                  {orphanCount > 0 && (
                    <option value={ORPHANS}>No parent ({orphanCount})</option>
                  )}
                  {parentEntries.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              )}
              <button
                className="admin-btn admin-btn-primary admin-btn-sm"
                onClick={handleAddNew}
              >
                + Add Entry
              </button>
            </div>
          </div>

          {/* Add/Edit Form */}
          {showForm && (
            <div style={{
              background: '#f8fafc',
              border: '1px solid var(--admin-border)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
            }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                {editingEntryId ? 'Edit Entry' : 'Add New Entry'}
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {!editingEntryId && (
                  <div className="admin-form-group">
                    <label className="admin-form-label">Code *</label>
                    <input
                      className="admin-form-input"
                      type="text"
                      value={form.code}
                      onChange={(e) => setForm({ ...form, code: e.target.value })}
                      placeholder="e.g., DXB"
                    />
                  </div>
                )}

                <div className="admin-form-group">
                  <label className="admin-form-label">Name (English) *</label>
                  <input
                    className="admin-form-input"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., Dubai"
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Name (Arabic)</label>
                  <input
                    className="admin-form-input"
                    type="text"
                    value={form.nameAr}
                    onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
                    placeholder="e.g., دبي"
                    dir="rtl"
                  />
                </div>

                {isHierarchical && (
                  <div className="admin-form-group">
                    <label className="admin-form-label">{parentCategory?.name} *</label>
                    <select
                      className="admin-select"
                      value={form.parentEntryId}
                      onChange={(e) => setForm({ ...form, parentEntryId: e.target.value })}
                      required
                    >
                      <option value="">-- Choose --</option>
                      {parentEntries.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="admin-form-group">
                  <label className="admin-form-label">Sort Order</label>
                  <input
                    className="admin-form-input"
                    type="number"
                    min="0"
                    value={form.sortOrder}
                    onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button
                  className="admin-btn admin-btn-primary admin-btn-sm"
                  disabled={(!editingEntryId && !form.code.trim()) || !form.name.trim() || (isHierarchical && !form.parentEntryId) || saving}
                  onClick={handleSave}
                >
                  {saving ? 'Saving...' : editingEntryId ? 'Update' : 'Create'}
                </button>
                <button
                  className="admin-btn admin-btn-outline admin-btn-sm"
                  onClick={() => { setShowForm(false); setEditingEntryId(null); setForm(emptyForm); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Entries Table */}
          {loadingEntries ? (
            <div className="admin-loading">
              <div className="admin-loading-spinner"></div>
            </div>
          ) : sortedEntries.length === 0 ? (
            <div className="admin-empty-state">
              <p>No entries found{parentFilter ? ' for this filter' : ''}. Click "+ Add Entry" to create one.</p>
            </div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Name (AR)</th>
                    {isHierarchical && <th>Parent</th>}
                    <th>Status</th>
                    <th style={{ width: '160px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.map((entry, index) => (
                    <tr key={entry.id}>
                      <td style={{ color: 'var(--admin-text-muted)', fontSize: '0.8rem' }}>{entry.sortOrder}</td>
                      <td><code style={{ fontSize: '0.8rem', background: '#f1f5f9', padding: '0.125rem 0.375rem', borderRadius: '4px' }}>{entry.code}</code></td>
                      <td>{entry.name}</td>
                      <td dir="rtl">{entry.nameAr}</td>
                      {isHierarchical && (
                        <td>
                          {isOrphan(entry) ? (
                            <span className="admin-badge rejected" title="Not listed anywhere in the app until it has a parent. Edit it to choose one.">
                              No parent
                            </span>
                          ) : (
                            parentEntries.find(p => p.id === entry.parentEntryId)?.name || '—'
                          )}
                        </td>
                      )}
                      <td>
                        <span className={`admin-badge ${entry.isActive ? 'approved' : 'rejected'}`}>
                          {entry.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          <button
                            className="admin-btn admin-btn-outline admin-btn-sm"
                            onClick={() => handleEdit(entry)}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          >
                            Edit
                          </button>
                          <button
                            className={`admin-btn admin-btn-sm ${entry.isActive ? 'admin-btn-outline' : 'admin-btn-primary'}`}
                            onClick={() => handleToggleActive(entry)}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          >
                            {entry.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="admin-btn admin-btn-outline admin-btn-sm"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.375rem' }}
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            className="admin-btn admin-btn-outline admin-btn-sm"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === sortedEntries.length - 1}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.375rem' }}
                            title="Move down"
                          >
                            ↓
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
